import { Partner } from '@/types/partner'
import { getIntegrationData } from './integrationDataUtilities'

export const supportedActionsForPartner = (partner: Partner) => {
  if (!partner) return ['form']
  // Define preferred order of actions in dropdown
  const actionOrder = ['form', 'list', 'tag', 'event', 'contact', 'automation']
  const availableFeatures = Object.keys(partner.espFeatureSupport)
    .filter((feature) => feature)
    .filter((feature) => actionOrder.includes(feature))
  // Sort by preferred order
  return availableFeatures.sort((a, b) => actionOrder.indexOf(a) - actionOrder.indexOf(b))
}

export const integrationDepedencies = (partner: Partner) => {
  // find any segment groups with sync ups
  const { segmentGroups } = useSegmentGroupStore()
  const syncUpSegmentGroups = segmentGroups.filter(
    (sg) =>
      sg.syncTo === partner.name &&
      sg.syncToCustomField &&
      sg.syncToCustomField !== ''
  )

  // find any segments with auto segments set up for the partner
  const autoSegments = segmentGroups.filter((sg) => {
    return sg.signals.some((signal) =>
      definitionLogicItems(signal.definition).some(
        (logic) => logic.$source === partner.name
      )
    )
  })

  // find any opt-in offers that use the partner
  const { optinOffers } = useOfferStore()
  const offersUsingPartner = optinOffers.filter((o) => offerCanBeSubmitted(o))

  // find any flows with conditions in place that reference the partner
  const { flows } = useFlowStore()
  const flowsReferencingPartner = flows.filter((f) => {
    let found = false

    funnelDefinitionEach(f.funnelDefinition, (node) => {
      if (found) return
      if (node.type === 'yesno') {
        found = definitionLogicItems(node.condition).some(
          (logic) => logic.$source === partner.name
        )
      }
    })

    return found
  })

  return {
    syncUpSegmentGroups,
    autoSegments,
    offersUsingPartner,
    flowsReferencingPartner
  }
}

export const swapOutEspPlaceholder = (partner: Partner) => {
  const { configAsString } = useConfigStore()
  if (!configAsString.includes('espplaceholder')) return

  const { flows } = useFlowStore()
  flows.forEach((f) => {
    funnelDefinitionEach(f.funnelDefinition, (node) => {
      if (node.type === 'yesno') {
        definitionLogicItems(node.condition).forEach((logic) => {
          if (logic.$source === 'espplaceholder') {
            logic.$source = partner.name
          }
        })
      }
    })
  })

  const { segmentGroups } = useSegmentGroupStore()
  segmentGroups.forEach((sg) => {
    sg.signals.forEach((signal) => {
      definitionLogicItems(signal.definition).forEach((logic) => {
        if (logic.$source === 'espplaceholder') {
          logic.$source = partner.name
        }
      })
    })
  })
}

export const customFieldName = () => {
  const integrationStore = useIntegrationStore()
  const { integratedPartner } = integrationStore
  return (
    integratedPartner?.espFeatureSupport?.customField?.label || 'Custom Field'
  )
}

export const customFieldLabel = async (field: string) => {
  const integrationStore = useIntegrationStore()
  const { integratedPartner } = integrationStore
  if (integratedPartner?.espFeatureSupport?.customField?.unstructured)
    return field

  try {
    const data = await getIntegrationData('partner', 'custom_fields')
    return data.value.find((f) => String(f.id) === field)?.label || field
  } catch (error) {
    console.warn('Failed to fetch custom field label:', error)
    return field
  }
}

export const customFieldKey = async (field: string) => {
  const integrationStore = useIntegrationStore()
  const { integratedPartner } = integrationStore
  // For unstructured integrations, the field ID is the key
  if (integratedPartner?.espFeatureSupport?.customField?.unstructured)
    return field

  const data = await getIntegrationData('partner', 'custom_fields')
  const fieldData = data.value.find((f) => String(f.id) === String(field))
  // Return templatingToken if available (for structured integrations like Bento), otherwise the field ID
  return fieldData?.templatingToken || field
}

export const tagLabel = async (tagId: string) => {
  const integrationStore = useIntegrationStore()
  const { integratedPartner } = integrationStore

  if (!integratedPartner?.espFeatureSupport?.tag) {
    return tagId
  }

  try {
    const data = await getIntegrationData('partner', 'tags')
    const tag = data.value.find((t) => String(t.id) === String(tagId))
    // Tags use 'label' property, not 'name'
    return tag?.label || tag?.name || tagId
  } catch (error) {
    console.warn('Failed to fetch tag label:', error)
    return tagId
  }
}

export const listLabel = async (listId: string) => {
  const integrationStore = useIntegrationStore()
  const { integratedPartner } = integrationStore

  if (!integratedPartner?.espFeatureSupport?.list) {
    return listId
  }

  try {
    const data = await getIntegrationData('partner', 'lists')
    const list = data.value.find((l) => String(l.id) === String(listId))
    // Lists might use 'label' or 'name' depending on the integration
    return list?.label || list?.name || listId
  } catch (error) {
    console.warn('Failed to fetch list label:', error)
    return listId
  }
}
